// src/pages/landlord-toolkit/AddManagedPropertyPage.tsx

import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, HomeIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../auth/firebaseAuth';
import LandlordToolkitLayout from '../../components/landlord-toolkit/LandlordToolkitLayout';
import {
  amenitiesList,
  berRatings,
  propertyTypes,
} from '../../components/property/property-card/PropertyCardTypes';
import type {
  ListingIntent,
  PropertyProgressStatus,
} from '../../components/property/property-card/PropertyCardTypes';
import { generateNumericPropertyId } from '../../utility/property-utilities/generateNumericPropertyId';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import { AnalyticsEvents } from '../../analytics/firebaseAnalytics';
import { uploadMultipleImages } from '../../utility/firebaseStorageHelpers';
import { convertHeicFilesToJpeg } from '../../utility/convertHeicToJpeg';
import toast from 'react-hot-toast';
import { updateSiteMetrics } from '../../utility/firebaseHelpers';

interface ManagedPropertyFormData {
  address: string;
  price: number;
  type: string;
  bedrooms: number;
  bathrooms: number;
  size?: number;
  berRating: string;
  amenities: string[];
  notes?: string;
  listingIntent: ListingIntent;
  propertyProgressStatus: PropertyProgressStatus;
}

const AddManagedPropertyPage: React.FC = () => {
  const { userProfile, currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const navigate = useNavigate();
  const analytics = useAnalyticsWithConsent();

  analytics.trackPixelPageView({
    email: currentUser?.email || undefined,
    externalId: currentUser?.uid,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ManagedPropertyFormData>({
    defaultValues: {
      address: '',
      price: undefined,
      type: 'Apartment',
      bedrooms: 1,
      bathrooms: 1,
      size: undefined,
      berRating: '',
      amenities: [],
      notes: '',
      listingIntent: 'rent',
      propertyProgressStatus: 'new',
    },
  });

  const selectedAmenities = watch('amenities');
  const currentListingIntent = watch('listingIntent');

  const toggleAmenity = (amenity: string) => {
    const currentAmenities = selectedAmenities || [];
    if (currentAmenities.includes(amenity)) {
      setValue(
        'amenities',
        currentAmenities.filter((a) => a !== amenity)
      );
    } else {
      setValue('amenities', [...currentAmenities, amenity]);
    }
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingImages(true);

      // Convert HEIC files to JPEG for browser compatibility
      const convertedFiles = await convertHeicFilesToJpeg(files);

      // Convert FileList to File array
      const fileArray = Array.from(convertedFiles);

      // Create preview URLs
      const previewUrls = fileArray.map((file) => URL.createObjectURL(file));

      setImageFiles(fileArray);
      setImagePreviewUrls(previewUrls);

      toast.success(`${fileArray.length} image(s) selected`);
    } catch (error) {
      console.error('Error processing images:', error);
      toast.error('Failed to process images. Please try again.');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index: number) => {
    // Revoke object URL to free memory
    URL.revokeObjectURL(imagePreviewUrls[index]);

    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit: SubmitHandler<ManagedPropertyFormData> = async (data) => {
    if (!currentUser) {
      setSubmitError('You must be logged in to add a property.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Track analytics
      analytics.logEvent(AnalyticsEvents.ADD_MANAGED_PROPERTY_STARTED, {
        user_id: currentUser.uid,
        property_type: data.type,
        bedrooms: data.bedrooms,
        bathrooms: data.bathrooms,
      });

      // Generate unique property ID
      const numericId = await generateNumericPropertyId();

      // Upload images if any
      let uploadedImageUrls: string[] = [];
      if (imageFiles.length > 0) {
        try {
          toast.loading('Uploading images...', { id: 'image-upload' });

          // Convert File[] to FileList-like object for uploadMultipleImages
          const dataTransfer = new DataTransfer();
          imageFiles.forEach((file) => dataTransfer.items.add(file));
          const fileList = dataTransfer.files;

          uploadedImageUrls = await uploadMultipleImages(fileList, `listings/${numericId}`, true);
          toast.success('Images uploaded successfully', { id: 'image-upload' });
        } catch (error) {
          console.error('Image upload error:', error);
          toast.error('Failed to upload images. Property will be created without images.');
          // Continue with property creation even if image upload fails
        }
      }

      // Call Cloud Function to create managed property
      const sanitizedSize =
        typeof data.size === 'number' && !Number.isNaN(data.size) ? data.size : undefined;

      const requestPayload = {
        id: numericId,
        propertyOwnerUid: currentUser.uid,
        address: data.address,
        price: data.price,
        type: data.type,
        bedrooms: data.bedrooms,
        bathrooms: data.bathrooms,
        size: sanitizedSize,
        berRating: data.berRating,
        amenities: data.amenities,
        notes: data.notes,
        images: uploadedImageUrls,
        status: 'managed',
        listingIntent: data.listingIntent,
        propertyProgressStatus: data.propertyProgressStatus,
        // dateAdded is handled by Cloud Function with serverTimestamp()
        user: {
          id: userProfile?.id || currentUser.uid,
          name: userProfile?.name || 'Unknown',
          email: userProfile?.email || currentUser.email || '',
          phone: userProfile?.phone || '',
          photoUrl: userProfile?.photoUrl || '',
        },
      };

      const response = await fetch(
        'https://europe-west1-rentsmart-6e31b.cloudfunctions.net/addManagedProperty',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestPayload),
        }
      );

      const responseText = await response.text();
      let responseJson: any = null;
      try {
        responseJson = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        console.warn('Failed to parse addManagedProperty response JSON', {
          parseError,
          responseText,
        });
      }

      if (!response.ok) {
        const errorMessage =
          responseJson?.message ||
          responseJson?.error ||
          responseJson?.details ||
          'Failed to add property';
        throw new Error(errorMessage);
      }

      const result = responseJson;

      // Track success
      analytics.logEvent(AnalyticsEvents.ADD_MANAGED_PROPERTY_COMPLETED, {
        user_id: currentUser.uid,
        property_id: result.propertyId,
        property_type: data.type,
      });

      try {
        await updateSiteMetrics({
          managedPropertiesAdded: 1,
        });
      } catch (error) {
        console.error('Failed to track lead:', error);
      }

      navigate('/landlord-toolkit/add-property/success', { replace: true });
    } catch (error: any) {
      console.error('Error adding managed property:', error);
      setSubmitError(error.message || 'Failed to add property. Please try again.');

      // Track error
      analytics.logEvent(AnalyticsEvents.ADD_MANAGED_PROPERTY_FAILED, {
        user_id: currentUser.uid,
        error_message: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LandlordToolkitLayout title="Add Managed Property">
      <div className="max-w-3xl mx-auto py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <HomeIcon className="w-16 h-16 text-primary mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Add Managed Property</h1>
          <p className="text-lg text-gray-600">
            Add a property to your private portfolio for rent tracking and tenant management
          </p>
          <p className="text-sm text-gray-500 mt-2">
            This property will NOT be publicly listed on RentSmart
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/landlord-toolkit/managed-properties')}
              className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-5 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50"
            >
              View Managed Properties
            </button>
            <button
              type="button"
              onClick={() => navigate('/landlord-toolkit')}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-green-500 to-green-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-green-500/40 transition hover:scale-[1.01]"
            >
              Back to Landlord Toolkit
            </button>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Property Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                Property Address *
              </label>
              <input
                {...register('address', {
                  required: 'Property address is required',
                })}
                type="text"
                id="address"
                placeholder="e.g., 123 Main Street, Dublin 2"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition ${
                  errors.address ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
              )}
            </div>

            {/* Listing Intent */}
            <div>
              <label
                htmlFor="listingIntent"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Listing Intent *
              </label>
              <select
                {...register('listingIntent', {
                  required: 'Listing intent is required',
                })}
                id="listingIntent"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition ${
                  errors.listingIntent ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {(['rent', 'sale', 'share'] as ListingIntent[]).map((intent) => (
                  <option key={intent} value={intent}>
                    {intent.charAt(0).toUpperCase() + intent.slice(1)}
                  </option>
                ))}
              </select>
              {errors.listingIntent && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.listingIntent.message as string}
                </p>
              )}
            </div>

            {/* Price */}
            <div>
              <label htmlFor="price" className="block text-sm font-semibold text-gray-700 mb-2">
                {currentListingIntent === 'sale' ? 'Sale Price (€) *' : 'Monthly Rent (€) *'}
              </label>
              <input
                {...register('price', {
                  required:
                    currentListingIntent === 'sale'
                      ? 'Sale price is required'
                      : 'Monthly rent is required',
                  min: { value: 1, message: 'Amount must be greater than 0' },
                  valueAsNumber: true,
                })}
                type="number"
                id="price"
                placeholder={currentListingIntent === 'sale' ? 'e.g., 350000' : 'e.g., 1500'}
                min="0"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition ${
                  errors.price ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>}
            </div>

            {/* Property Type */}
            <div>
              <label htmlFor="type" className="block text-sm font-semibold text-gray-700 mb-2">
                Property Type *
              </label>
              <select
                {...register('type', { required: 'Property type is required' })}
                id="type"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition ${
                  errors.type ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {propertyTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>}
            </div>

            {/* Property Progress Status */}

            {/* Property Progress Status */}
            <div>
              <label
                htmlFor="propertyProgressStatus"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Property Progress Status *
              </label>
              <select
                {...register('propertyProgressStatus', {
                  required: 'Property progress status is required',
                })}
                id="propertyProgressStatus"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition ${
                  errors.propertyProgressStatus ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {(['new', 'viewing-in-progress', 'sale-agreed'] as PropertyProgressStatus[]).map(
                  (status) => (
                    <option key={status} value={status}>
                      {status === 'new'
                        ? 'New'
                        : status === 'viewing-in-progress'
                          ? 'Viewing In Progress'
                          : 'Sale Agreed'}
                    </option>
                  )
                )}
              </select>
              {errors.propertyProgressStatus && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.propertyProgressStatus.message as string}
                </p>
              )}
            </div>

            {/* Bedrooms and Bathrooms */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="bedrooms"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Bedrooms *
                </label>
                <input
                  {...register('bedrooms', {
                    required: 'Number of bedrooms is required',
                    min: { value: 0, message: 'Minimum 0 bedrooms' },
                    max: { value: 20, message: 'Maximum 20 bedrooms' },
                    valueAsNumber: true,
                  })}
                  type="number"
                  id="bedrooms"
                  min="0"
                  max="20"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition ${
                    errors.bedrooms ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.bedrooms && (
                  <p className="mt-1 text-sm text-red-600">{errors.bedrooms.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="bathrooms"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Bathrooms *
                </label>
                <input
                  {...register('bathrooms', {
                    required: 'Number of bathrooms is required',
                    min: { value: 0, message: 'Minimum 0 bathrooms' },
                    max: { value: 10, message: 'Maximum 10 bathrooms' },
                    valueAsNumber: true,
                  })}
                  type="number"
                  id="bathrooms"
                  min="0"
                  max="10"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition ${
                    errors.bathrooms ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.bathrooms && (
                  <p className="mt-1 text-sm text-red-600">{errors.bathrooms.message}</p>
                )}
              </div>
            </div>

            {/* Size (optional) */}
            <div>
              <label htmlFor="size" className="block text-sm font-semibold text-gray-700 mb-2">
                Size (sqm) - Optional
              </label>
              <input
                {...register('size', {
                  min: { value: 1, message: 'Minimum 1 sqm' },
                  max: { value: 10000, message: 'Maximum 10,000 sqm' },
                  setValueAs: (value) =>
                    value === '' || value === null ? undefined : Number(value),
                })}
                type="number"
                id="size"
                placeholder="e.g., 85"
                min="1"
                max="10000"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition ${
                  errors.size ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.size && <p className="mt-1 text-sm text-red-600">{errors.size.message}</p>}
            </div>

            {/* BER Rating */}
            <div>
              <label htmlFor="berRating" className="block text-sm font-semibold text-gray-700 mb-2">
                BER Rating - Optional
              </label>
              <select
                {...register('berRating')}
                id="berRating"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
              >
                <option value="">Select BER Rating (Optional)</option>
                {berRatings.map((rating) => (
                  <option key={rating} value={rating}>
                    {rating}
                  </option>
                ))}
              </select>
            </div>

            {/* Amenities */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Amenities - Optional
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {amenitiesList.map((amenity) => (
                  <button
                    key={amenity}
                    type="button"
                    onClick={() => toggleAmenity(amenity)}
                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                      selectedAmenities?.includes(amenity)
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-primary'
                    }`}
                  >
                    {amenity}
                  </button>
                ))}
              </div>
            </div>

            {/* Property Images */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Property Images - Optional
              </label>
              <div className="space-y-4">
                {/* Upload Button */}
                <div className="flex items-center justify-center w-full">
                  <label
                    htmlFor="image-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg
                        className="w-10 h-10 mb-3 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, HEIC up to 10MB each</p>
                    </div>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/heic,image/webp"
                      multiple
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Image Previews */}
                {imagePreviewUrls.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {imagePreviewUrls.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove image"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {imageFiles.length > 0 && (
                  <p className="text-sm text-gray-600">{imageFiles.length} image(s) selected</p>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-2">
                Private Notes - Optional
              </label>
              <textarea
                {...register('notes')}
                id="notes"
                rows={4}
                placeholder="Add any private notes about this property (tenant history, maintenance notes, etc.)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
              />
            </div>

            {/* Error Message */}
            {submitError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
              >
                <p className="text-sm font-medium">{submitError}</p>
              </motion.div>
            )}

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/landlord-toolkit')}
                className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting
                  ? uploadingImages
                    ? 'Uploading Images...'
                    : 'Adding Property...'
                  : 'Add Property'}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-blue-900 mb-2">What is a Managed Property?</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Private property for your personal management - NOT publicly listed</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Track rent payments, send reminders, and manage tenants</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Only visible to you in your Landlord Toolkit dashboard</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Perfect for properties you've already rented out or manage privately</span>
            </li>
          </ul>
        </motion.div>
      </div>
    </LandlordToolkitLayout>
  );
};

export default AddManagedPropertyPage;
